// React and Hooks
import React, { useEffect, useReducer, useRef } from 'react'

// Material-UI Components
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { Box, Typography } from '@mui/material'
import { Delete } from '@mui/icons-material'

// Custom Components
import Layout from '../../../layouts/common/Layout'
import LiveSearch from '../LiveSearch/LiveSearch'
import InputField from '../InputField/InputField'
import LinkButton from '../LinkButton'
import LoadingButton from '../LoadingButton'

// Constants and Utilities
import { ACTIONS, API_URL } from '../../../api/constants'
import ROUTES from '../../../route/routes'

// Helper function to initialize value state for a single row
const getInitialValueState = (initialArgs, fields) => {
  const initialValueState = fields.reduce(
    (valueState, currentField) => ({
      ...valueState,
      ...([currentField?.name] && { [currentField.name]: '' }),
    }),
    {}
  )
  return { ...initialArgs, ...initialValueState }
}

// Helper function to initialize value states for multiple rows
const getInitialValueStates = (initialArgs, rows, multi) => {
  if (multi) {
    return rows.reduce((valueStates, row) => {
      const { id, fields } = row
      return {
        ...valueStates,
        [id]: getInitialValueState(initialArgs, fields),
      }
    }, {})
  }
  const data = getInitialValueState(initialArgs, rows)
  return data
}

// Helper function to initialize error state for a single row
const getInitialErrorState = (initialArgs, fields) => {
  const initialErrorState = fields.reduce((errorState, currentField) => {
    const { name, validator, required } = currentField
    return { ...errorState, [name]: validator || required ? false : true }
  }, {})
  return { ...initialArgs, ...initialErrorState }
}

// Helper function to initialize error states for multiple rows
const getInitialErrorStates = (initialArgs, rows, multi) => {
  if (multi) {
    return rows.reduce((errorStates, row) => {
      const { id, fields } = row
      return {
        ...errorStates,
        [id]: getInitialErrorState(initialArgs, fields),
      }
    }, {})
  }
  return getInitialErrorState(initialArgs, rows)
}

// Reducer to manage value state updates
const valueReducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case ACTIONS.INIT:
      return payload
    case ACTIONS.UPDATE:
      const { name, value, rowId } = payload
      if (rowId) {
        return {
          ...state,
          [rowId]: {
            ...(state && state[rowId] ? state[rowId] : {}),
            [name]: value,
          },
        }
      }
      return { ...state, [name]: value }

    default:
      return state
  }
}

// Reducer to manage error state updates
const errorReducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case ACTIONS.INIT:
      return payload
    case ACTIONS.UPDATE:
      const { name, error, rowId } = payload
      if (rowId) {
        return {
          ...state,
          [rowId]: {
            ...state[rowId],
            [name]: error,
          },
        }
      }
      return { ...state, [name]: error }
    default:
      return state
  }
}

// Function to render individual fields
const renderFields = (fields, config, id) =>
  fields.map((field) => {
    const {
      valueState,
      handleChange,
      errorState,
      handleError,
      handleKproTeamSelect,
      readOnlyFields = [],
      isViewMode,
    } = config
    const { name, component } = field
    const value = id ? valueState[id] && valueState[id][name] : valueState[name]
    const error = id ? errorState[id] && errorState[id][name] : errorState[name]
    const encryptValue = id
      ? valueState[id] && valueState[id]['encrypted']
      : valueState['encrypted']

    const FieldComponent = component || InputField
    if (name === 'kpro_team_id') {
      const facilityName = id
        ? valueState[id] && valueState[id]['facility_name']
        : valueState['facility_name']

      return (
        <LiveSearch
          key={id ? `${id}-${name}` : name}
          {...field}
          onSelect={handleKproTeamSelect}
          targetName="facility_name"
          value={value}
          targetValue={facilityName}
          onChange={handleChange}
          error={error}
          onError={handleError}
          rowId={id}
          url={API_URL.TEAMS}
          optionLabelProp={{
            primary: 'kpro_id',
            secondary: 'name',
          }}
          searchQueryProp="name"
          idProp="kpro_id"
          dataProperty="listOfTeams"
          noOptionsText="No Teams Available"
          readOnly={readOnlyFields.includes(name) || isViewMode}
        />
      )
    }
    return (
      <FieldComponent
        key={id ? `${id}-${name}` : name}
        {...field}
        encryptValue={encryptValue}
        state={valueState}
        value={value}
        onChange={handleChange}
        error={error}
        onError={handleError}
        rowId={id}
        readOnly={
          (encryptValue && encryptValue === true) ||
          readOnlyFields.includes(name) ||
          isViewMode
        }
      />
    )
  })

// Function to render rows of fields
const renderRows = (rows, config) =>
  rows.map((row) => {
    const { id, fields } = row
    const { handleRowDelete } = config
    const fieldElements = renderFields(fields, config, id)
    return (
      <Layout.ColumnContainer
        columnCount={5}
        lastColumnWidth="56px"
        key={id}
        marginBottom={3}
      >
        {fieldElements}
        <IconButton
          title="Delete Config"
          sx={{
            marginLeft: 'auto',
            fontSize: '32px',
          }}
          onClick={() => {
            handleRowDelete(id)
          }}
          key={'actions'}
        >
          <Delete fontSize="32px" />
        </IconButton>
      </Layout.ColumnContainer>
    )
  })

// Helper function to check if fields are multi-row
const isMultiRow = (fields) =>
  Array.isArray(fields) && fields.length > 0 && Array.isArray(fields[0].fields)

// Function to render field groups
const renderFieldGroups = (fieldGroups, config) => {
  return Object.keys(fieldGroups)
    .sort((a, b) => {
      return a?.localeCompare(b)
    })
    .map((groupName) => {
      const fields = fieldGroups[groupName]
      const multi = isMultiRow(fields)
      const fieldElements = multi
        ? renderRows(fields, config)
        : renderFields(fields, config)
      return (
        <Box key={groupName} sx={{ marginBottom: '24px' }}>
          <Typography
            variant="h6"
            sx={{
              marginBottom: '16px',
              marginTop: '16px',
              textTransform: 'capitalize',
              fontWeight: 'bold',
            }}
          >
            {fields[0].heading}
          </Typography>
          {fieldElements}
        </Box>
      )
    })
}

// Helper function to check if all values in an object are truthy
const isTruthyObject = (object) =>
  Object.values(object).every((value) => value === true)

// Function to determine if the form is valid
const getFormValidState = (errorState, isNested) => {
  if (Object.keys(errorState).length === 0) return false
  if (isNested) {
    return Object.keys(errorState).every((key) =>
      isTruthyObject(errorState[key])
    )
  }
  return isTruthyObject(errorState)
}

// Main Form component
const Form = ({
  fields,
  children,
  initialData,
  columnCount,
  onDelete,
  onSubmit,
  loading,
  readOnlyFields,
  viewMode,
  getTeamId,
  disabled = false,
  fieldGroups,
}) => {
  const isViewMode = viewMode === 'VIEW'

  const multi = isMultiRow(fields)
  const [valueState, setValueState] = useReducer(
    valueReducer,
    {},
    (initialArgs) => getInitialValueStates(initialArgs, fields, multi)
  )
  const [errorState, setErrorState] = useReducer(
    errorReducer,
    {},
    (initialArgs) => getInitialErrorStates(initialArgs, fields, multi)
  )
  const changedData = useRef({})

  // Function to track changed data
  const handleChangedData = (payload) => {
    changedData.current = valueReducer(changedData.current, {
      type: ACTIONS.UPDATE,
      payload,
    })
  }

  // Effect to initialize value state with initial data
  useEffect(() => {
    if (initialData) {
      setValueState({
        type: 'INIT',
        payload: initialData,
      })
    }
  }, [initialData])

  // Handler for team selection
  const handleKproTeamSelect = (
    value,
    targetValue,
    practice_timezone,
    connected_application,
    rowId
  ) => {
    const kproPayload = {
      name: 'kpro_team_id',
      value: value,
      rowId,
    }

    setValueState({
      type: ACTIONS.UPDATE,
      payload: kproPayload,
    })

    handleChangedData(kproPayload)

    const facilityNamePayload = {
      name: 'facility_name',
      value: targetValue,
      rowId,
    }
    setValueState({
      type: ACTIONS.UPDATE,
      payload: facilityNamePayload,
    })
    typeof getTeamId === 'function' && getTeamId(value)
    handleChangedData(facilityNamePayload)

    const practiceTimezonePayload = {
      name: 'practice_timezone',
      value: practice_timezone,
      rowId,
    }
    setValueState({
      type: ACTIONS.UPDATE,
      payload: practiceTimezonePayload,
    })

    handleChangedData(practiceTimezonePayload)

    const connected_applicationPayload = {
      name: 'connected_application',
      value: connected_application,
      rowId,
    }
    setValueState({
      type: ACTIONS.UPDATE,
      payload: connected_applicationPayload,
    })

    handleChangedData(connected_applicationPayload)
  }

  // Handler for field value changes
  const handleChange = (e, rowId) => {
    const { name, value } = e.target
    setValueState({
      type: ACTIONS.UPDATE,
      payload: { name, value, rowId },
    })
    handleChangedData({ name, value, rowId })
  }

  // Handler for field error changes
  const handleError = (name, error, rowId) => {
    setErrorState({
      type: ACTIONS.UPDATE,
      payload: { name, error, rowId },
    })
  }

  // Handler for form submission
  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(valueState, changedData.current)
    changedData.current = null
  }

  // Handler for row deletion
  const handleRowDelete = (id) => {
    onDelete(id)
    const valueStateCopy = { ...valueState }
    delete valueStateCopy[id]
    setValueState({
      type: ACTIONS.INIT,
      payload: valueStateCopy,
    })

    const errorStateCopy = { ...errorState }
    delete errorStateCopy[id]
    setErrorState({
      type: ACTIONS.INIT,
      payload: errorStateCopy,
    })
  }

  // Determine if the form is valid
  const isFormValid = getFormValidState(errorState, multi)

  // Configuration object for rendering fields
  const config = {
    valueState,
    errorState,
    handleChange,
    handleError,
    handleKproTeamSelect,
    handleRowDelete,
    readOnlyFields,
    isViewMode,
  }

  // Function to render form content
  const renderFormContent = () => {
    if (fieldGroups) {
      return renderFieldGroups(fieldGroups, config)
    }
    return (
      <Layout.ColumnContainer
        columnCount={columnCount}
        animate
        display={multi ? 'block' : 'flex'}
        disabled={disabled}
      >
        {multi ? renderRows(fields, config) : renderFields(fields, config)}
      </Layout.ColumnContainer>
    )
  }

  // Render the form
  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      style={{
        marginTop: '24px',
      }}
    >
      {renderFormContent()}
      {children}
      <Stack direction="row" gap={3} marginTop={3}>
        <LinkButton variant="outlined" to={ROUTES.ALL.FACILITIES}>
          Cancel
        </LinkButton>

        <LoadingButton
          type="submit"
          variant="contained"
          disabled={!isFormValid || isViewMode}
          loading={loading}
        >
          Save
        </LoadingButton>
      </Stack>
    </form>
  )
}

export default Form
